import * as React from "react";

/**
 * Tiny vendor-free theme provider. Replaces `next-themes` for the Vite
 * SPAs (which have no Next.js runtime). Mirrors the same public API so
 * components ported from a Next-based portal don't need to change.
 *
 * Behaviour:
 *  - Resolves `Theme` (user preference: light | dark | system) into
 *    `ResolvedTheme` (actual rendered: light | dark) via `matchMedia`.
 *  - Persists the user preference to `localStorage` under `STORAGE_KEY`.
 *  - Reflects the resolved theme on `<html data-theme="...">` so
 *    `tokens.css` rules in `:root[data-theme="dark"]` activate.
 *  - Listens for OS-level scheme changes when preference is `system`.
 */

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "eos:theme";
const ATTRIBUTE = "data-theme";

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme !== "system") return theme;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(ATTRIBUTE, resolved);
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** Default preference on first paint (before localStorage hydration). */
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme = "system" }: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<Theme>(defaultTheme);
  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(() =>
    resolveTheme(defaultTheme),
  );

  // Hydrate from localStorage after mount (avoids SSR/hydration mismatch even
  // though we're a CSR-only Vite SPA — keeping the pattern consistent).
  React.useEffect(() => {
    const stored = readStoredTheme();
    setThemeState(stored);
    const r = resolveTheme(stored);
    setResolvedTheme(r);
    applyTheme(r);
  }, []);

  // Re-resolve when OS preference flips and user is on `system`.
  React.useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r: ResolvedTheme = mql.matches ? "dark" : "light";
      setResolvedTheme(r);
      applyTheme(r);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = React.useCallback((next: Theme) => {
    setThemeState(next);
    const r = resolveTheme(next);
    setResolvedTheme(r);
    applyTheme(r);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const value = React.useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}
